import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useCurrentUserStore } from '@/stores/useCurrentUserStore'

/**
 * useAPI — central authenticated fetch wrapper.
 *
 * Handles auth headers, token refresh, and 401 retries automatically.
 * Always use this instead of raw fetch.
 *
 * @returns {{
 *   dashboardAPI:          (path: string, options?: RequestInit) => Promise<any>,
 *   mrfInventoryAPI:       (path: string, options?: RequestInit) => Promise<any>,
 *   mrfPublicAPI:          (path: string, options?: RequestInit) => Promise<any>,
 *   documentsAPI:          (path: string, options?: RequestInit) => Promise<any>,
 *   relationshipsAPI:      (path: string, options?: RequestInit) => Promise<any>,
 *   ticExtractorAPI:       (path: string, options?: RequestInit) => Promise<any>,
 *   myelinAPI:             (path: string, options?: RequestInit) => Promise<any>,
 *   isFetching:            import('vue').Ref<boolean>,
 *   baseFetch:             (url: string, options?: RequestInit) => Promise<Response>,
 *   setAuthorizationHeader:(options?: RequestInit) => RequestInit,
 * }}
 *
 * Usage:
 *   const { dashboardAPI } = useAPI()
 *   const data = await dashboardAPI('/some/path', { method: 'POST', body: { key: 'value' } })
 *
 * `body` objects are automatically JSON-serialized.
 * Pass `{ rawResponse: true }` to get the `Response` back instead of parsed JSON (e.g. file downloads); token
 * refresh, the 401 retry and error throwing still apply.
 * Token refresh and redirect to /sign-in on auth failure are handled internally.
 */
export function useAPI() {
    const currentUser = useCurrentUserStore()
    const router = useRouter()
    const isFetching = ref(false)
    const fetchError = ref(null)

    function setAuthorizationHeader(options = {}) {
        return {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${currentUser.token()}`,
            },
        }
    }

    async function baseFetch(url, options = {}) {
        if (options.body) {
            options = {
                ...options,
                headers: {
                    ...options.headers,
                    'content-type': 'application/json',
                },
                body: JSON.stringify(options.body),
            }
        }

        return await fetch(url, options)
    }

    async function refreshAndRedirect() {
        try {
            await currentUser.authenticate()
            return true
        } catch {
            isFetching.value = false
            if (router) {
                router.push('/sign-in')
            } else {
                window.location.href = '/sign-in'
            }
            return false
        }
    }

    async function fetchWithRetry(url, options) {
        isFetching.value = true

        if (!options.disableTokenRefresh && currentUser.areTokensExpired()) {
            const refreshed = await refreshAndRedirect()
            if (!refreshed) return
            options = setAuthorizationHeader(options)
        }

        let response = await baseFetch(url, options)

        if (response.status === 401 && !options.disableTokenRefresh) {
            const refreshed = await refreshAndRedirect()
            if (!refreshed) return
            options = setAuthorizationHeader(options)
            response = await baseFetch(url, options)
        }

        if (!response.ok) {
            isFetching.value = false
            const errorText = await response.text()
            fetchError.value = errorText
            throw new Error(errorText)
        }

        if (options.rawResponse) {
            isFetching.value = false
            return response
        }

        try {
            return await response.json()
        } catch {
            return null
        } finally {
            isFetching.value = false
        }
    }

    /**
     * Create and Return API wrappers for the different endpoints
     */

    const dashboardAPI = async (path, options) => {
        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_DASHBOARD}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    const ticExtractorAPI = async (path, options) => {
        const authOptions = setAuthorizationHeader(options)

        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_TIC_EXTRACTOR}${path}`, {
            ...authOptions,
            headers: {
                ...authOptions.headers,
                'x-api-key': import.meta.env.VITE_API_URL_TIC_EXTRACTOR_KEY,
            },
        })
    }

    const mrfInventoryAPI = async (path, options) => {
        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_MRF_INDEXER}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    const mrfPublicAPI = async (path, options) => {
        const baseURL = import.meta.env.VITE_API_URL_MRF_INDEXER.replace(/\/api\/?$/, '').replace(/\/+$/, '')
        return await fetchWithRetry(`${baseURL}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    const documentsAPI = async (path, options) => {
        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_DOCUMENTS}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    const relationshipsAPI = async (path, options) => {
        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_PROVIDER_DIRECTORY}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    const myelinAPI = async (path, options) => {
        return await fetchWithRetry(`${import.meta.env.VITE_API_URL_MYELIN}${path}`, {
            ...setAuthorizationHeader(options),
        })
    }

    return {
        dashboardAPI,
        ticExtractorAPI,
        mrfInventoryAPI,
        mrfPublicAPI,
        documentsAPI,
        relationshipsAPI,
        myelinAPI,
        baseFetch,
        setAuthorizationHeader,
        isFetching,
    }
}
